import React, { useMemo, useRef } from 'react';
import { useIntl } from 'umi';
import { useHover } from 'ahooks';
import { Col, Card, Row } from 'tea-component';
import { Bubble } from '@home/components/TeaComponents';
import Loading from '@/pages/intelligence/components/Loading';
import CompetitorsCard from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/GameInfo/Competitors/components/CompetitorsCard';
import OperationIcon from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/GameInfo/components/OperationIcon';
import { TypeMap } from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/GameInfo/Competitors/_config';
import { downloadAction } from '@intelligence/components/DownloadCommon/DownloadPage';
import useGameComparison from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/hooks/useGameComparison';
import GameComparisonIcon from '@intelligence/components/GameComparisonIcon';

import './index.scss';

interface IGameItem {
  // 游戏封面
  cover: string;
  // 游戏名称
  name: string;
  // 图片
  image: string;
  // 时间
  time: string;
  // 发行者
  publisher: string;
  // 唯一 id
  app_id: string;
  // 发行国家
  country: string;
  // 副品类
  sub_genre: string;
  entity_type: string;
}

interface propInfo {
  // 是否上线
  isRelease: boolean;
  // 游戏列表
  similarGames: IGameItem[];
  // 游戏类型
  gameType?: string;
  // 目前的 tab 模块名称 overview、stats、storerank
  activeId: string;
  // 目前的游戏类型名称 mobileGames、pcGames、consoleGames
  // moduleName: string;
  entityType: string;
  setDetailIndex: any;
  dLoading: any;
  title?: string;
  gameName?: string;
  // Current game ID for VS comparison
  gameId?: string;
}

export default function Competitors(props: propInfo) {
  const {
    isRelease,
    similarGames = [],
    activeId,
    // moduleName,
    setDetailIndex,
    dLoading,
    entityType,
    title,
    gameName,
    gameId,
  } = props;

  const { formatMessage } = useIntl(); // 中英切换方法

  // Game Comparison hook - handles VS button click
  const { handleVSClick } = useGameComparison({
    entityType,
    gameId: gameId || '',
  });

  const ref = useRef(null);
  const isHovering = useHover(ref);

  const emptyTipsLocaleKey = isRelease
    ? 'dbi_detail_similar_games_nodata_tips'
    : 'dbi_detail_game_not_launch_tips';

  const downloadData = useMemo(() => {
    return similarGames?.map((item) => {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      const [sub_genre_first, sub_genre_second] = item?.sub_genre?.split('|') || [];
      const type = TypeMap[item?.entity_type];
      return {
        ...item,
        type,
        sub_genre_first,
        sub_genre_second,
      };
    });
  }, [similarGames]);

  const handleDownload = () => {
    const fileName = `${gameName}'s Competitors List`;

    downloadAction({
      downLoadParams: {
        downloadData,
        file_format: 0,
        fileName,
      },
      downloadKey: 'competitors',
    });
  };

  // Show VS button only when there are competitors and current game ID
  const showVSButton = gameId && similarGames?.length > 0;

  return (
    <Card ref={ref}>
      <Card.Body
        title={
          <div className="dbi-competitors-title">
            <div className="dbi-competitors-title-text">{title}</div>
            {isHovering && (
              <div className="dbi-competitors-title-actions">
                {showVSButton && (
                  <Bubble
                    dark
                    placement="top"
                    className="dbi-competitors-vs-bubble"
                    content={formatMessage({ id: 'dbi_vs_button_tooltip' })}
                  >
                    <button
                      type="button"
                      className="dbi-competitors-vs-button"
                      onClick={() => handleVSClick(similarGames)}
                    >
                      <span className="dbi-competitors-vs-button-text">
                        {formatMessage({ id: 'dbi_game_comparison' })}
                      </span>
                      <GameComparisonIcon
                        className="dbi-competitors-vs-button-icon"
                      />
                    </button>
                  </Bubble>
                )}
                <OperationIcon
                  className="dbi-competitors-download-icon"
                  type="download"
                  onClick={handleDownload}
                />
              </div>
            )}
          </div>
        }
      >
        {dLoading ? (
          <>
            <div
              style={{
                height: 200,
                color: '#444',
              }}
            >
              <Loading />
            </div>
          </>
        ) : (
          <Row>
            {similarGames?.length > 0 ? (
              similarGames?.map((item: any, index: number) => (
                <Col span={8} key={index}>
                  <CompetitorsCard
                    gameData={item}
                    activeId={activeId}
                    setDetailIndex={setDetailIndex}
                    entityType={entityType}
                  />
                </Col>
              ))
            ) : (
              <div
                style={{
                  height: '144px',
                  lineHeight: '144px',
                  textAlign: 'center',
                  width: '100%',
                }}
              >
                {formatMessage({ id: emptyTipsLocaleKey })}
              </div>
            )}
          </Row>
        )}
      </Card.Body>
    </Card>
  );
}
